/**
 * Shared user data cache utility
 * Stores common user information across all forms on the site
 */

const USER_DATA_CACHE_KEY = 'atom_user_data_cache';

// Fields to share across forms
const SHARED_FIELDS = ['nom', 'prenom', 'email', 'telephone', 'date_naissance', 'dateNaissance', 'adresse', 'npa', 'ville', 'npa_localite'];

/**
 * Load cached user data from localStorage
 * @returns {Object} Cached user data or empty object
 */
export const loadUserDataCache = () => {
  try {
    const cached = localStorage.getItem(USER_DATA_CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (e) {
    console.warn('Erreur lors du chargement du cache utilisateur:', e);
  }
  return {};
};

/**
 * Save user data to localStorage cache
 * @param {Object} data - User data to cache
 */
export const saveUserDataCache = (data) => {
  try {
    const currentCache = loadUserDataCache();
    const updatedCache = { ...currentCache };
    
    // Only save shared fields that have values
    SHARED_FIELDS.forEach(field => {
      if (data[field] && data[field].toString().trim()) {
        updatedCache[field] = data[field];
      }
    });
    
    // Handle nested locataire object (for GarantieLoyer form)
    if (data.locataire) {
      if (data.locataire.nom) updatedCache.nom = data.locataire.nom;
      if (data.locataire.prenom) updatedCache.prenom = data.locataire.prenom;
      if (data.locataire.email) updatedCache.email = data.locataire.email;
      if (data.locataire.telephone) updatedCache.telephone = data.locataire.telephone;
      if (data.locataire.dateNaissance) updatedCache.dateNaissance = data.locataire.dateNaissance;
      if (data.locataire.adresse) updatedCache.adresse = data.locataire.adresse;
      if (data.locataire.npa) updatedCache.npa = data.locataire.npa;
      if (data.locataire.ville) updatedCache.ville = data.locataire.ville;
    }
    
    localStorage.setItem(USER_DATA_CACHE_KEY, JSON.stringify(updatedCache));
  } catch (e) {
    console.warn('Erreur lors de la sauvegarde du cache utilisateur:', e);
  }
};

/**
 * Merge cached user data with form defaults
 * @param {Object} defaults - Default form values
 * @param {Object} fieldMapping - Optional mapping of cache fields to form fields
 * @returns {Object} Merged form data
 */
export const mergeWithUserCache = (defaults, fieldMapping = {}) => {
  const cached = loadUserDataCache();
  const merged = { ...defaults };
  
  // Direct field mapping
  Object.keys(cached).forEach(cacheField => {
    const formField = fieldMapping[cacheField] || cacheField;
    if (merged.hasOwnProperty(formField) && cached[cacheField]) {
      merged[formField] = cached[cacheField];
    }
  });
  
  return merged;
};

/**
 * Merge cached user data with nested form structure (like GarantieLoyer)
 * @param {Object} defaults - Default form values with nested structure
 * @returns {Object} Merged form data
 */
export const mergeWithUserCacheNested = (defaults) => {
  const cached = loadUserDataCache();
  const merged = JSON.parse(JSON.stringify(defaults)); // Deep clone
  
  // If form has locataire nested object
  if (merged.locataire) {
    if (cached.nom) merged.locataire.nom = cached.nom;
    if (cached.prenom) merged.locataire.prenom = cached.prenom;
    if (cached.email) merged.locataire.email = cached.email;
    if (cached.telephone) merged.locataire.telephone = cached.telephone;
    if (cached.dateNaissance) merged.locataire.dateNaissance = cached.dateNaissance;
    if (cached.adresse) merged.locataire.adresse = cached.adresse;
    if (cached.npa) merged.locataire.npa = cached.npa;
    if (cached.ville) merged.locataire.ville = cached.ville;
  }
  
  return merged;
};

export default {
  loadUserDataCache,
  saveUserDataCache,
  mergeWithUserCache,
  mergeWithUserCacheNested
};
